import { Link } from 'react-router-dom';
import { Calendar, Award, Sparkles, ShieldCheck } from 'lucide-react';
import SEO from '../../components/SEO';

export default function OmDanshakerDe() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'Über Danshaker',
    description: 'Die Geschichte hinter dem Danshaker, seit 2019 entwickelt von Rasmus Gangsted-Rasmussen.',
    url: 'https://danshaker.dk/de/ueber-danshaker',
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Über Danshaker - Die Geschichte der dänischen Entmistungsmaschine"
        description="Erfahren Sie mehr über Danshaker, entwickelt von Rasmus Gangsted-Rasmussen in Randers, Dänemark. Höchste dänische Qualität für leichtere Stallarbeit."
        path="/de/ueber-danshaker"
        schema={aboutSchema}
        lang="de"
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Main Content */}
      <section className="mx-auto max-w-7xl px-4 pt-16 pb-20 md:px-8 md:pt-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Column: Info */}
          <div className="lg:col-span-7 space-y-6">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Die Geschichte hinter dem Original</span>
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
                Über Danshaker
              </h1>
            </div>

            <div className="space-y-4 text-slate-700 leading-relaxed text-base md:text-lg font-medium">
              <p>
                Mein Name ist Rasmus Gangsted-Rasmussen und ich habe 2019 mit der Entwicklung des Danshakers begonnen. Der Anstoß war der dringende Wunsch, meinen Angestellten und Einstellern auf meinem damaligen Pferdebetrieb Stald Fredenshøj in Randers die schwere und kräftezehrende Handarbeit beim Ausmisten abzunehmen. Zudem wollte ich testen, wie viel Zeit und Geld sich im Alltag realistisch einsparen lassen.
              </p>
              <p>
                Der Weg des Danshakers zu dem ausgereiften Qualitätsprodukt von heute war lang und voller Herausforderungen. Doch dank engagierter Partner aus dem Maschinenbau und der festen Entschlossenheit, eine kompromisslos funktionierende Lösung zu bauen, ist es gelungen.
              </p>
              <p>
                Was mich besonders antreibt: Zu sehen, wie viel Lebensqualität meine Kunden durch den Danshaker zurückgewinnen. Die Maschine übernimmt die anstrengende Rüttel- und Siebarbeit komplett – körperliche Überlastungen und Rückenschmerzen gehören der Vergangenheit an.
              </p>
              <p>
                Gleichzeitig sparen Pferdehalter bis zu 50 % Einstreu und halbieren ihre Arbeitszeit pro Box. Das schenkt wertvolle Stunden für das, worauf es wirklich ankommt: die Pferde.
              </p>
            </div>

            <div className="pt-4 flex flex-wrap items-center gap-4">
              <Link
                to="/de/kostenlose-demo"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0E67A2] px-6 py-3.5 font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-md"
              >
                <Calendar className="h-5 w-5" />
                Kostenlose Demo buchen
              </Link>
              <Link
                to="/de/kontakt"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3.5 font-bold text-slate-800 transition hover:bg-slate-100 shadow-xs"
              >
                Kontakt aufnehmen
              </Link>
            </div>
          </div>

          {/* Right Column: Visual / Trust Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-xl">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/82c2389c-2509-456f-8e9c-aa5c44a15f3e.jpg"
                alt="Rasmus Gangsted-Rasmussen und Danshaker"
                width="600"
                height="450"
                loading="lazy"
                decoding="async"
                className="rounded-2xl object-cover w-full h-[380px]"
              />
              <div className="p-4">
                <p className="font-bold text-slate-900 text-lg">Rasmus Gangsted-Rasmussen</p>
                <p className="text-sm text-slate-600 font-semibold">Gründer & Entwickler des Danshakers</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex items-center gap-3">
                <Award className="h-8 w-8 text-[#0E67A2] shrink-0" />
                <div>
                  <p className="font-bold text-slate-900 text-sm">Dänische Fertigung</p>
                  <p className="text-xs text-slate-500 font-medium">Entwickelt & montiert in Randers</p>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex items-center gap-3">
                <ShieldCheck className="h-8 w-8 text-[#0E67A2] shrink-0" />
                <div>
                  <p className="font-bold text-slate-900 text-sm">Robuste Qualität</p>
                  <p className="text-xs text-slate-500 font-medium">Gebaut für harten Stalleinsatz</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
